import { useEffect } from 'react';
import { useAolStore } from '../context/AolContext';
import { SectionType } from '../types/ViewModel/AolSectionType';
import { useAuthStore } from '../context/AuthContext';
import { NoonReportStatusEnum, NoonReportTypeEnum } from '../types/ViewModel/AolEnums';
import { useAolUpdateHandler } from '../app/useAolUpdateHandler';
import { AolSectionSignalRDTO } from '../types/Dto/AolSectionSignalRDTO';
import { ValidatedNoonReportFactory } from '../factory/ValidatedNoonReportFactory';
import { ValidatedNoonReport } from '../types/ViewModel/ValidatedNoonReport';
import { useVesselStore } from '../context/VesselContext';
import { NoonReportFuelRobSectionBM } from '../types/BusinessModel/ValidatedNoonReportBM';
import useApi from './useApi';
import { buildQueryParams } from './endpoints/queryParamsHelper';
import useApiEndpoints from '../context/useApiEndpoints';
import { DateTimeFormat, formatDateTime } from 'odfjell-date-time-utils/dateTimeFormatter';
import { createUtcTimeAdjustTimezones } from '../utils/event/eventDateUtils';
import { useRotationStore } from '../context/RotationContext';
import { TankersEvent } from '../types/ViewModel/TankersEvent';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import useSyncOfflineData from 'src/utils/connectivity/useSyncOfflineData';
import { isNullOrUndefined } from 'src/utils/objectUtils';
import { formatAolUrlDate } from 'src/utils/aol/aolDateUtils';
import useGetAolData from './aol/useGetAolData';
import useGetRotation from './useGetRotation';
import { useStowageStore } from 'src/context/StowageContext';
import useAppFeature from 'src/app/useAppFeature';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import { getAolPortCall } from 'src/utils/aol/aolPortCallUtils';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { selectedVessel } = useVesselStore();
    const { selectedAolDataItem, noonReports, aolFilter, aolEventsArray } = useAolStore();
    const { signalRConnectionId } = useAuthStore();
    const { syncItemsIfHasOfflineNoonReports } = useSyncOfflineData();
    const { processApiEndpointError } = apiEndpointUtils();
    const { updateAolList, addNoonReport, updateOnDelete, updateOnHoursSync, updateOnImosPortCallIdSync, updateOnFuelRobSync } =
        useAolUpdateHandler();
    const { state, postApiData } = useApi();
    const validatedNoonReportFactory = new ValidatedNoonReportFactory();
    const { getAolData, ensureAolEventsArrayLoaded } = useGetAolData();
    const { updatedRotation, rotation, timeZoneData } = useRotationStore();
    const { loadedMasterStowagePlan } = useStowageStore();
    const { getExtendedRotation } = useGetRotation();
    const { isFeatureEnabled } = useAppFeature();
    useEffect(() => {
        (async () => {
            if (state.status === 'success') {
                if (state.endpoint === apiEndpoints['syncNoonReportPortLogHours'].url) {
                    const vm = validatedNoonReportFactory.create(state.result);
                    updateOnHoursSync(vm);
                } else if (state.endpoint === apiEndpoints['syncNoonReportImosPortCallId'].url) {
                    updateOnImosPortCallIdSync(state.additionalInfo.noonReportId, state.additionalInfo.imosPortCallId);
                } else if (state.endpoint === apiEndpoints['syncNoonReportFuelRob'].url) {
                    updateOnFuelRobSync(state.result);
                } else if (state.endpoint === apiEndpoints['saveNoonReportSection'].url) {
                    let addNoonReportDTO;
                    if (state.additionalInfo.noonReport) {
                        addNoonReportDTO = await addNoonReport(state.additionalInfo.noonReport);
                    }
                    const dto: AolSectionSignalRDTO = {
                        section: state.additionalInfo?.section,
                        noonReportId: state.additionalInfo.noonReportId,
                        imoNumber: state.additionalInfo.imoNumber,
                        indexInDay: state.additionalInfo.indexInDay,
                        metadata: state.result?.metadata,
                    };
                    updateAolList(dto, addNoonReportDTO);
                }
                if (state.endpoint === apiEndpoints['deleteNoonReport'].url) {
                    updateOnDelete(state.additionalInfo.noonReportId.toString());
                }
            } else {
                processApiEndpointError(state);
            }
        })();
    }, [state]);

    const refreshAolDataIfNeeded = async (shouldRefresh: boolean): Promise<void> => {
        if (shouldRefresh && selectedVessel) {
            await getAolData(selectedVessel.imoNumber, formatAolUrlDate(aolFilter.fromDate), formatAolUrlDate(aolFilter.toDate), true);
        }
    };

    const syncNoonReportAndCheckForDataRefresh = async (
        noonReport: ValidatedNoonReport | null,
        noonReportId?: number,
        shouldSync: boolean = true
    ): Promise<{ syncedNoonReportId: number; shouldRefreshData: boolean }> => {
        if (!shouldSync) {
            return { syncedNoonReportId: noonReportId!, shouldRefreshData: false };
        }

        const originalId = noonReport?.metadata.noonReportId ?? noonReportId!;
        const syncedNoonReportId = await syncItemsIfHasOfflineNoonReports(originalId);
        const shouldRefreshData = originalId !== syncedNoonReportId;

        if (shouldRefreshData && noonReport) {
            noonReport.metadata.noonReportId = syncedNoonReportId;
        }

        return { syncedNoonReportId, shouldRefreshData };
    };

    const syncFuelRobMismatch = async (noonReport: ValidatedNoonReport, fuelRobSection: NoonReportFuelRobSectionBM) => {
        if (!selectedVessel) return;

        const { syncedNoonReportId, shouldRefreshData } = await syncNoonReportAndCheckForDataRefresh(noonReport);

        await postApiData({
            endpoint: apiEndpoints['syncNoonReportFuelRob'].url,
            data: fuelRobSection,
            queryParam: buildQueryParams({
                imoNumber: selectedVessel.imoNumber,
                connectionId: signalRConnectionId,
                noonReportId: syncedNoonReportId,
            }),
        });

        await refreshAolDataIfNeeded(shouldRefreshData);
    };

    const syncAolPortLogHoursMismatch = async (noonReport: ValidatedNoonReport, portLogHours: number) => {
        if (!selectedVessel) return;

        const { syncedNoonReportId, shouldRefreshData } = await syncNoonReportAndCheckForDataRefresh(noonReport);

        await postApiData({
            endpoint: apiEndpoints['syncNoonReportPortLogHours'].url,
            queryParam: buildQueryParams({
                imoNumber: selectedVessel.imoNumber,
                connectionId: signalRConnectionId,
                noonReportId: syncedNoonReportId,
                portLogHours,
            }),
        });

        await refreshAolDataIfNeeded(shouldRefreshData);
    };

    const syncNoonReportImosPortCallIdMismatch = async (noonReport: ValidatedNoonReport, imosPortCallId: number) => {
        if (!selectedVessel) return;

        const { syncedNoonReportId, shouldRefreshData } = await syncNoonReportAndCheckForDataRefresh(noonReport);

        await postApiData({
            endpoint: apiEndpoints['syncNoonReportImosPortCallId'].url,
            queryParam: buildQueryParams({
                imoNumber: selectedVessel.imoNumber,
                connectionId: signalRConnectionId,
                noonReportId: syncedNoonReportId,
                imosPortCallId,
            }),
            additionalInfo: { noonReportId: noonReport.metadata.noonReportId, imosPortCallId },
        });

        await refreshAolDataIfNeeded(shouldRefreshData);
    };

    const saveAolSection = async (
        imoNumber: number,
        indexInDay: number,
        numberOfReportsInDay: number,
        imosPortCallId: number,
        noonReportSection: SectionType,
        noonReportId?: number
    ) => {
        let noonReport: ValidatedNoonReport | null = null;
        if (selectedAolDataItem?.status === NoonReportStatusEnum.Missing) {
            const startTimeLocalTime = selectedAolDataItem.aolItem?.portLogAolItem.startTimeLocalTime;
            const stopTimeLocalTime = selectedAolDataItem.aolItem?.portLogAolItem.stopTimeLocalTime;

            noonReport = await saveAol(
                imoNumber,
                indexInDay,
                numberOfReportsInDay,
                imosPortCallId,
                startTimeLocalTime,
                stopTimeLocalTime,
                selectedAolDataItem.reportType,
                selectedAolDataItem.reportHours,
                selectedAolDataItem.useNewConsumptionModel ? 5 : 3
            );

            if (noonReport) {
                noonReportId = noonReport.metadata.noonReportId;
            }
        }

        const { syncedNoonReportId, shouldRefreshData } = await syncNoonReportAndCheckForDataRefresh(
            noonReport,
            noonReportId,
            !isNullOrUndefined(noonReportId)
        );

        if (!isNullOrUndefined(noonReportId)) {
            noonReportId = syncedNoonReportId;
        }

        const section = noonReportSection as any;
        if (!isNullOrUndefined(section.noonReportId)) {
            section.noonReportId = noonReportId;
        }

        const result = await postApiData({
            endpoint: apiEndpoints['saveNoonReportSection'].url,
            data: noonReportSection,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId, noonReportId: noonReportId }),
            additionalInfo: {
                section: noonReportSection,
                noonReportId: noonReportId,
                indexInDay,
                imoNumber,
                additionalLoaderData: { section: section.sectionData.section },
                noonReport,
            },
        });

        await refreshAolDataIfNeeded(shouldRefreshData);

        if (result.isOfflineResponse) {
            if (!noonReport && result.noonReportId < 0) {
                noonReport = validatedNoonReportFactory.createNewOfflineReport(result);
            } else if (!noonReport && +result.noonReportId > 0) {
                noonReport = noonReports.find((n) => n.metadata.noonReportId === +result.noonReportId);
            }
            return validatedNoonReportFactory.updateOfflineReport(noonReport, result);
        }

        return validatedNoonReportFactory.create(result);
    };

    const saveAol = async (
        imoNumber: number,
        indexInDay: number,
        numberOfReportsInDay: number,
        imosPortCallId: number,
        reportFromTimeLocalTime: Date,
        reportToTimeLocalTime: Date,
        noonReportType: NoonReportTypeEnum,
        aolHours: number,
        aolVersion: number
    ) => {
        const fromLocalTime = formatDateTime(reportFromTimeLocalTime, DateTimeFormat.queryFormats.dateTime);
        const toLocalTime = formatDateTime(reportToTimeLocalTime, DateTimeFormat.queryFormats.dateTime);

        // Get port call for timezone calculation
        const isStowageIntegrationActive = isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber });
        const firstVoyageSync = selectedVessel?.firstVoyageOrcaWebStowageImosSync;

        const portCall = await getAolPortCall(
            imoNumber,
            imosPortCallId,
            isStowageIntegrationActive,
            firstVoyageSync,
            loadedMasterStowagePlan,
            timeZoneData,
            updatedRotation,
            rotation,
            getExtendedRotation
        );

        const events = await ensureAolEventsArrayLoaded(imoNumber, formatAolUrlDate(aolFilter.fromDate), formatAolUrlDate(aolFilter.toDate));

        // Create minimal event object for conversion function
        const tempEvent = {
            portCallId: imosPortCallId,
            eventId: null,
        } as TankersEvent;

        // Calculate UTC using port timezone (not browser timezone)
        const startTimeUtcDate = createUtcTimeAdjustTimezones(portCall ?? null, tempEvent, reportFromTimeLocalTime, events ?? aolEventsArray);
        const stopTimeUtcDate = createUtcTimeAdjustTimezones(portCall ?? null, tempEvent, reportToTimeLocalTime, events ?? aolEventsArray);

        const startTimeUtc = formatDateTime(startTimeUtcDate, DateTimeFormat.queryFormats.dateTime);
        const stopTimeUtc = formatDateTime(stopTimeUtcDate, DateTimeFormat.queryFormats.dateTime);

        const result = await postApiData({
            endpoint: apiEndpoints['addNoonReport'].url,
            queryParam: buildQueryParams({
                imoNumber,
                connectionId: signalRConnectionId,
                indexInDay,
                numberOfReportsInDay,
                imosPortCallId,
                reportFromTimeLocalTime: fromLocalTime,
                reportToTimeLocalTime: toLocalTime,
                reportFromTimeUTC: startTimeUtc,
                reportToTimeUTC: stopTimeUtc,
                noonReportType,
                aolHours,
                aolVersion,
            }),
            additionalInfo: {
                additionalLoaderData: { selectedAolDataItemId: selectedAolDataItem?.id },
            },
        });
        return result.isOfflineResponse ? validatedNoonReportFactory.createNewOfflineReport(result) : validatedNoonReportFactory.create(result);
    };

    const deleteAol = async (imoNumber: number, noonReportId: number) => {
        noonReportId = await syncItemsIfHasOfflineNoonReports(noonReportId);

        const { shouldRefreshData } = await syncNoonReportAndCheckForDataRefresh(null, noonReportId, !isNullOrUndefined(noonReportId));

        await postApiData({
            endpoint: apiEndpoints['deleteNoonReport'].url,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId, noonReportId }),
            additionalInfo: { noonReportId },
        });
        await refreshAolDataIfNeeded(shouldRefreshData);
    };

    return {
        saveAolSection,
        saveAol,
        deleteAol,
        syncAolPortLogHoursMismatch,
        syncNoonReportImosPortCallIdMismatch,
        syncFuelRobMismatch,
    };
};
